import React, { createContext, useState, useEffect, useContext } from 'react';
import { useTranslation } from 'react-i18next';
import { authApi, languageApi, settingsApi } from '../services/api';

// Create context
const AuthContext = createContext();

// Custom hook to use the auth context
export const useAuth = () => {
  return useContext(AuthContext);
};

// Provider component
export const AuthProvider = ({ children }) => {
  const [currentUser, setCurrentUser] = useState(null);
  const [token, setToken] = useState(localStorage.getItem('token'));
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const { i18n } = useTranslation();

  // No need for axios interceptor here as it's handled in api.js

  // Load user data on mount or token change
  useEffect(() => {
    const loadUser = async () => {
      if (!token) {
        setCurrentUser(null);
        setLoading(false);
        return;
      }

      try {
        const response = await authApi.getCurrentUser();
        setCurrentUser(response.data);
        
        // Set language based on user preference
        if (response.data.language) {
          i18n.changeLanguage(response.data.language);
        }
        
        setError(null);
      } catch (err) {
        console.error('Failed to load user:', err);
        setError('Session expired. Please login again.');
        logout();
      } finally {
        setLoading(false);
      }
    };

    loadUser();
  }, [token, i18n]);

  // Login function - now just updates the state with user data
  const login = (userData) => {
    // Update token state to trigger the useEffect
    setToken(localStorage.getItem('token'));
    setCurrentUser(userData);
    
    // Set language based on user preference
    if (userData.language) {
      i18n.changeLanguage(userData.language);
    }
    
    setError(null);
    return userData;
  };

  // Logout function
  const logout = () => {
    localStorage.removeItem('token');
    setToken(null);
    setCurrentUser(null);
    setError(null);
  };

  // Check if user has a specific role
  const hasRole = (role) => {
    return currentUser?.role === role;
  };

  // Check if user has permission for a specific action
  const hasPermission = (resource, action) => {
    if (!currentUser) return false;
    
    const role = currentUser.role;
    
    // Owner can do everything
    if (role === 'owner') return true;
    
    // Admin can do everything except user management
    if (role === 'admin') {
      if (resource === 'users' || resource === 'audit_logs') return false;
      return true;
    }
    
    // Member Manager permissions
    if (role === 'member_manager') {
      // Can only work with members and payments (no delete)
      if ((resource === 'members' || resource === 'payments') && action !== 'delete') {
        return true;
      }
      // Can view reports
      if (resource === 'reports' && action === 'view') {
        return true;
      }
      // Cannot work with expenses, users, or audit logs
      return false;
    }
    
    // Viewers can only view resources
    if (role === 'viewer') {
      return action === 'view';
    }
    
    return false;
  };

  // Update user language preference
  const updateLanguage = async (language) => {
    try {
      await languageApi.updatePreference(language);
      
      // Update current user state with new language
      setCurrentUser(prev => ({
        ...prev,
        language
      }));
      
      // Update i18n language
      i18n.changeLanguage(language);
      
      return true;
    } catch (err) {
      console.error('Failed to update language preference:', err);
      return false;
    }
  };

  // Fetch application name from settings
  const [appName, setAppName] = useState('Duebit');
  
  useEffect(() => {
    // Only fetch app name if user is logged in
    if (currentUser && (currentUser.role === 'owner' || currentUser.role === 'admin')) {
      const fetchAppName = async () => {
        try {
          const response = await settingsApi.getSettings();
          const settings = response.data;
          
          if (settings.APP_NAME && settings.APP_NAME.value) {
            setAppName(settings.APP_NAME.value);
            
            // Update the translation resources
            const resources = i18n.getResourceBundle(i18n.language, 'common');
            if (resources && resources.app) {
              resources.app.name = settings.APP_NAME.value;
              i18n.addResourceBundle(i18n.language, 'common', resources, true, true);
              
              // Also update other languages
              const otherLang = i18n.language === 'en' ? 'de' : 'en';
              const otherResources = i18n.getResourceBundle(otherLang, 'common');
              if (otherResources && otherResources.app) {
                otherResources.app.name = settings.APP_NAME.value;
                i18n.addResourceBundle(otherLang, 'common', otherResources, true, true);
              }
            }
          }
        } catch (err) {
          console.error('Failed to fetch app name:', err);
        }
      };
      
      fetchAppName();
    }
  }, [currentUser, i18n]);

  // Context value
  const value = {
    currentUser,
    loading,
    error,
    login,
    logout,
    hasRole,
    hasPermission,
    updateLanguage,
    appName
  };

  return (
    <AuthContext.Provider value={value}>
      {children}
    </AuthContext.Provider>
  );
};
